export interface MappedCompanyRow {
  name: string;
  phone_number: string;
  website: string;
  linkedin_url: string;
  email: string;
  instagram: string;
  tiktok: string;
  facebook: string;
  youtube: string;
  maps_url: string;
  address: string;
  city: string;
  country: string;
  rating: number | null;
  reviews_count: number | null;
  extraNotes: string;
}

const BULLET_KEYS = [
  "barrio",
  "localidad",
  "departamento_estado",
  "clasificacion_presencia",
  "tiene_web_oficial",
  "horarios",
  "reviews_1_estrella",
  "reviews_2_estrellas",
  "reserva_en_google",
  "proveedor_agenda",
  "url_reserva",
  "agenda_detectada_en",
  "linktree",
  "twitter_x",
];

function cellToString(value: unknown): string {
  if (value == null) {
    return "";
  }
  if (typeof value === "string") {
    return value.trim();
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return String(value).trim();
}

function normalizeKey(key: string): string {
  return key
    .replace(/^\uFEFF/, "")
    .trim()
    .toLowerCase();
}

function pick(row: Record<string, unknown>, keys: string[]): string {
  const normalizedEntries = Object.entries(row).map(([rowKey, value]) => [
    normalizeKey(rowKey),
    cellToString(value),
  ]);
  for (const key of keys) {
    const wanted = normalizeKey(key);
    const match = normalizedEntries.find(([rowKey]) => rowKey === wanted);
    if (match?.[1]) {
      return match[1];
    }
  }
  return "";
}

function isMissingValue(value: string): boolean {
  return !value || /^(no|no tiene|n\/a|na|null|undefined)$/i.test(value);
}

function asWebsite(value: string): string {
  const text = value.trim();
  if (isMissingValue(text)) {
    return "";
  }
  if (/google\.com\/maps|maps\.google|maps\.app\.goo\.gl/i.test(text)) {
    return "";
  }
  if (/^https?:\/\//i.test(text)) {
    return text.slice(0, 300);
  }
  if (/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}(\/.*)?$/i.test(text)) {
    return `https://${text}`;
  }
  return "";
}

function asSocialUrl(value: string): string {
  const text = value.trim();
  if (isMissingValue(text)) {
    return "";
  }
  if (/^https?:\/\//i.test(text)) {
    return text.slice(0, 400);
  }
  if (
    /^(www\.|instagram\.com|facebook\.com|tiktok\.com|youtube\.com|linkedin\.com)/i.test(
      text,
    )
  ) {
    return `https://${text}`;
  }
  if (/^@/.test(text)) {
    return text;
  }
  return text.slice(0, 400);
}

function asMapsUrl(value: string): string {
  const text = value.trim();
  if (isMissingValue(text)) {
    return "";
  }
  if (/^https?:\/\//i.test(text)) {
    return text.slice(0, 500);
  }
  return "";
}

function asNumber(value: string): number | null {
  if (isMissingValue(value)) {
    return null;
  }
  const parsed = Number(value.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function fallbackName(row: Record<string, unknown>): string {
  for (const value of Object.values(row)) {
    const text = cellToString(value);
    if (text.length < 2) {
      continue;
    }
    if (/^https?:\/\//i.test(text)) {
      continue;
    }
    if (/^[\d+\s().-]{7,}$/.test(text)) {
      continue;
    }
    return text;
  }
  return "";
}

function extraBullets(row: Record<string, unknown>): string {
  const notes: string[] = [];
  for (const key of BULLET_KEYS) {
    const text = pick(row, [key]);
    if (!text) {
      continue;
    }
    if (text.length > 280) {
      continue;
    }
    notes.push(`- ${key}: ${text}`);
  }
  return notes.join("\n");
}

export function mapCompanyImportRow(
  row: Record<string, unknown>,
): MappedCompanyRow {
  const name =
    pick(row, [
      "name",
      "nombre",
      "title",
      "business_name",
      "empresa",
      "company",
      "place_name",
      "negocio",
      "razon_social",
      "razón social",
      "local",
      "store",
    ]) || fallbackName(row);

  return {
    name,
    phone_number: pick(row, [
      "phone",
      "phone_number",
      "telefono",
      "teléfono",
      "phones",
      "mobile",
      "whatsapp",
    ]),
    website: asWebsite(
      pick(row, ["web_oficial", "website", "web", "site", "domain"]),
    ),
    linkedin_url: asSocialUrl(pick(row, ["linkedin", "linkedin_url"])),
    email: pick(row, ["email", "correo", "mail"]),
    instagram: asSocialUrl(pick(row, ["instagram"])),
    tiktok: asSocialUrl(pick(row, ["tiktok"])),
    facebook: asSocialUrl(pick(row, ["facebook"])),
    youtube: asSocialUrl(pick(row, ["youtube"])),
    maps_url: asMapsUrl(pick(row, ["maps_url", "link"])),
    address: pick(row, [
      "address",
      "full_address",
      "full address",
      "dirección",
      "direccion",
      "street",
    ]),
    city: pick(row, ["city", "ciudad", "municipality"]),
    country: pick(row, ["pais", "país", "country"]),
    rating: asNumber(pick(row, ["rating", "review_rating"])),
    reviews_count: asNumber(pick(row, ["reviews_count", "review_count"])),
    extraNotes: extraBullets(row),
  };
}
